/**
 * Dialog reutilizável de seleção de template.
 *
 * Usado pelo Broadcast (Step "Conteúdo") e pelo Flow Builder (BlocksEditor)
 * pra "Usar modelo da biblioteca". Click numa linha → onSelect(template) +
 * fecha o dialog. O caller é responsável por converter via `templateToBlocks`
 * e aplicar no estado.
 *
 * 2 tabs (igual à tela /meta/template-library):
 *   - "Explorar": catálogo TI rankeado, com paginação e toggle de favoritos
 *   - "Meus templates": pastas próprias + chips de projeto pra navegar escopo
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    BookOpen, ChevronLeft, ChevronRight, ChevronDown,
    Compass, Eye, Files, Folder, FolderTree as FolderTreeIcon,
    Image as ImageIcon, Loader2, MessageSquare, Search, Star, User, X,
} from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    listLibraryTemplates,
    listFavoriteTemplateIds,
    getFolderTree,
    listCustomTemplates,
    getProjects,
} from '@/services/api';
import type {
    TemplateLibraryItem, CustomTemplate, TemplateFolderNode,
} from '@/types/template-library';

type Tab = 'explore' | 'mine';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelect: (template: TemplateLibraryItem) => void;
    /** Filtra inicialmente por tipo (útil quando o caller só aceita um tipo). */
    initialType?: 'any' | 'generic' | 'button';
}

const PER_PAGE = 30;

/** Converte um CustomTemplate ("Meus templates") pro shape de TemplateLibraryItem
 *  pra reusar o mesmo callback `onSelect`/`templateToBlocks` dos callers. */
function customToLibraryItem(c: CustomTemplate): TemplateLibraryItem {
    return {
        template_id: c.id,
        template_type: c.template_type,
        title: c.title ?? null,
        subtitle: c.subtitle ?? null,
        text_content: c.text_content ?? null,
        image_url: c.image_url ?? null,
        buttons_json: c.buttons_json ?? null,
        domain: null,
        url_path: null,
        utm_campaign: null,
        language: null,
        project_id: c.project_id ?? null,
        status: null,
        is_favorite: false,
    };
}

export default function TemplatePickerDialog({
    open, onOpenChange, onSelect, initialType = 'any',
}: Props) {
    const [tab, setTab] = useState<Tab>('explore');
    const [searchInput, setSearchInput] = useState('');
    const [debouncedQ, setDebouncedQ] = useState('');
    const [page, setPage] = useState(1);
    const [onlyFavorites, setOnlyFavorites] = useState(false);
    // Filtros adicionais da tab 'explore'
    const [filterType, setFilterType] = useState<'any' | 'generic' | 'button'>('any');

    // Tab 'mine' state — projeto + pasta selecionados
    const [mineProjectId, setMineProjectId] = useState<number | null>(null);
    const [mineFolderId, setMineFolderId] = useState<number | null>(null);

    // Debounce do search
    useEffect(() => {
        const h = setTimeout(() => setDebouncedQ(searchInput.trim()), 400);
        return () => clearTimeout(h);
    }, [searchInput]);

    // Reset ao abrir
    useEffect(() => {
        if (open) {
            setSearchInput('');
            setDebouncedQ('');
            setPage(1);
            setTab('explore');
            setOnlyFavorites(false);
            setFilterType(initialType);
            setMineProjectId(null);
            setMineFolderId(null);
        }
    }, [open, initialType]);

    // Reset paginação ao mudar tab/search/filtros
    useEffect(() => { setPage(1); }, [tab, debouncedQ, onlyFavorites, filterType]);

    // Reset folder quando muda projeto
    useEffect(() => { setMineFolderId(null); }, [mineProjectId]);

    // ─── Tab 'explore': catálogo TI rankeado ──────────────────────
    const { data: exploreData, isLoading: exploreLoading, isFetching: exploreFetching } = useQuery({
        queryKey: ['template-picker-explore', debouncedQ, filterType, page, onlyFavorites],
        queryFn: () => listLibraryTemplates({
            q: debouncedQ || undefined,
            template_type: filterType,
            only_favorites: onlyFavorites,
            period: 'all',          // Mostra todos os modelos (não só últimos 30 dias)
            per_page: PER_PAGE,
            page,
        }),
        enabled: open && tab === 'explore',
        staleTime: 1000 * 30,
    });

    const { data: favIds } = useQuery({
        queryKey: ['template-library-favorites'],
        queryFn: listFavoriteTemplateIds,
        enabled: open,
        staleTime: 1000 * 60 * 5,
    });

    // ─── Tab 'mine': projetos + pastas + templates próprios ─────────
    const { data: projects = [] } = useQuery({
        queryKey: ['template-picker-projects'],
        queryFn: getProjects,
        enabled: open && tab === 'mine',
        staleTime: 1000 * 60 * 5,
    });

    const { data: folderTree, isLoading: foldersLoading } = useQuery({
        queryKey: ['template-picker-folders', mineProjectId],
        queryFn: () => getFolderTree(mineProjectId),
        enabled: open && tab === 'mine',
        staleTime: 1000 * 30,
    });

    const { data: customResp, isLoading: customLoading, isFetching: customFetching } = useQuery({
        queryKey: ['template-picker-custom', mineProjectId, mineFolderId],
        queryFn: () => listCustomTemplates(mineProjectId, mineFolderId),
        enabled: open && tab === 'mine',
        staleTime: 1000 * 30,
    });

    const favoriteSet = useMemo(
        () => new Set(favIds?.template_ids ?? []),
        [favIds],
    );

    const exploreItems = exploreData?.items ?? [];
    const explorePages = exploreData?.pages ?? 1;
    const exploreTotal = exploreData?.total ?? 0;

    // Filtra templates da pasta pela busca local
    const customItems = useMemo(() => {
        const all = customResp?.items ?? [];
        if (!debouncedQ) return all;
        const q = debouncedQ.toLowerCase();
        return all.filter((c) => (
            (c.name || '').toLowerCase().includes(q) ||
            (c.title || '').toLowerCase().includes(q) ||
            (c.subtitle || '').toLowerCase().includes(q) ||
            (c.text_content || '').toLowerCase().includes(q)
        ));
    }, [customResp, debouncedQ]);

    const handlePick = (template: TemplateLibraryItem) => {
        onSelect(template);
        onOpenChange(false);
    };

    const handlePickCustom = (c: CustomTemplate) => {
        onSelect(customToLibraryItem(c));
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[860px] max-h-[90vh] overflow-hidden flex flex-col p-0">
                <DialogHeader className="p-4 pb-3 border-b">
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <BookOpen className="w-4 h-4 text-violet-600" />
                        Usar modelo da biblioteca
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        Selecione um modelo pra aplicar como ponto de partida. Você pode editar depois.
                    </DialogDescription>
                </DialogHeader>

                {/* Tabs */}
                <div className="px-4 pt-3 pb-2 border-b">
                    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 w-fit">
                        <button
                            type="button"
                            onClick={() => setTab('explore')}
                            className={`px-3 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5 ${
                                tab === 'explore'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <Compass className="w-3.5 h-3.5" />
                            Explorar
                        </button>
                        <button
                            type="button"
                            onClick={() => setTab('mine')}
                            className={`px-3 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5 ${
                                tab === 'mine'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <FolderTreeIcon className="w-3.5 h-3.5" />
                            Meus templates
                        </button>
                    </div>
                </div>

                {/* Search + filtros específicos da tab */}
                <div className="px-4 pt-3 pb-3 border-b space-y-2">
                    {/* Chips de projeto (só na tab 'mine') */}
                    {tab === 'mine' && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] text-gray-500 uppercase font-medium tracking-wide mr-1">
                                Projeto:
                            </span>
                            <ProjectChip
                                label="Pessoal"
                                active={mineProjectId == null}
                                onClick={() => setMineProjectId(null)}
                            />
                            {projects.map((p) => (
                                <ProjectChip
                                    key={p.id}
                                    label={p.name}
                                    active={mineProjectId === p.id}
                                    onClick={() => setMineProjectId(p.id)}
                                />
                            ))}
                        </div>
                    )}

                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder={tab === 'mine'
                                    ? 'Buscar nome, título, subtítulo...'
                                    : 'Buscar título, subtítulo, texto...'
                                }
                                className="pl-8 h-9"
                                autoFocus
                            />
                            {searchInput && (
                                <button
                                    type="button"
                                    onClick={() => setSearchInput('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                        {tab === 'explore' && (
                            <button
                                type="button"
                                onClick={() => setOnlyFavorites(!onlyFavorites)}
                                title={onlyFavorites ? 'Mostrando só favoritos — clique pra ver todos' : 'Mostrar só favoritos'}
                                className={`h-9 px-2.5 inline-flex items-center gap-1.5 rounded-md border text-xs font-medium transition-colors ${
                                    onlyFavorites
                                        ? 'bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100'
                                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                <Star
                                    className={`w-3.5 h-3.5 ${
                                        onlyFavorites
                                            ? 'fill-amber-400 text-amber-500'
                                            : 'text-gray-400'
                                    }`}
                                />
                                Favoritos
                                {favoriteSet.size > 0 && (
                                    <span className="ml-0.5 opacity-70">({favoriteSet.size})</span>
                                )}
                            </button>
                        )}
                    </div>

                    {/* Filtros adicionais (só 'explore') — chips de tipo */}
                    {tab === 'explore' && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] text-gray-500 uppercase font-medium tracking-wide mr-1">
                                Tipo:
                            </span>
                            <TypeChip
                                label="Todos"
                                active={filterType === 'any'}
                                onClick={() => setFilterType('any')}
                            />
                            <TypeChip
                                label="Card (com imagem)"
                                active={filterType === 'generic'}
                                onClick={() => setFilterType('generic')}
                            />
                            <TypeChip
                                label="Texto + botões"
                                active={filterType === 'button'}
                                onClick={() => setFilterType('button')}
                            />
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-hidden flex">
                    {tab === 'mine' ? (
                        <>
                            {/* Sidebar com pastas */}
                            <FoldersSidebar
                                folders={folderTree?.folders ?? []}
                                rootTemplateCount={folderTree?.root_template_count ?? 0}
                                selectedFolderId={mineFolderId}
                                onSelectFolder={setMineFolderId}
                                loading={foldersLoading}
                            />
                            {/* Lista de templates da pasta */}
                            <div className="flex-1 overflow-y-auto p-2">
                                {customLoading ? (
                                    <ListSkeleton />
                                ) : customItems.length === 0 ? (
                                    <EmptyState tab={tab} hasQuery={!!debouncedQ} />
                                ) : (
                                    <div className="space-y-1.5">
                                        {customItems.map((c) => (
                                            <CustomTemplateRow
                                                key={c.id}
                                                template={c}
                                                onClick={() => handlePickCustom(c)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 overflow-y-auto p-2">
                            {exploreLoading ? (
                                <ListSkeleton />
                            ) : exploreItems.length === 0 ? (
                                <EmptyState tab={tab} hasQuery={!!debouncedQ} />
                            ) : (
                                <div className="space-y-1.5">
                                    {exploreItems.map((t) => (
                                        <TemplateRow
                                            key={t.template_id}
                                            template={t}
                                            isFavorite={favoriteSet.has(t.template_id)}
                                            onClick={() => handlePick(t)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer: paginação (só 'explore') + cancelar */}
                <div className="border-t px-4 py-2 text-[11px] text-gray-500 flex items-center justify-between gap-2">
                    {tab === 'mine' ? (
                        <span>
                            {customItems.length > 0 && (
                                <>
                                    {customItems.length}{' '}
                                    {customItems.length === 1 ? 'modelo' : 'modelos'}
                                    {customResp && customItems.length < customResp.total && (
                                        <span className="ml-1 text-gray-400">
                                            (filtrado de {customResp.total})
                                        </span>
                                    )}
                                </>
                            )}
                            {customFetching && <Loader2 className="w-3 h-3 animate-spin inline ml-2" />}
                        </span>
                    ) : (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1 || exploreFetching}
                                className="h-7 px-2"
                            >
                                <ChevronLeft className="w-3.5 h-3.5" />
                            </Button>
                            <span className="text-[11px] text-gray-600 min-w-[68px] text-center">
                                Pág. {page} / {Math.max(explorePages, 1)}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage((p) => Math.min(explorePages, p + 1))}
                                disabled={page >= explorePages || exploreFetching}
                                className="h-7 px-2"
                            >
                                <ChevronRight className="w-3.5 h-3.5" />
                            </Button>
                            <span className="text-[11px] text-gray-500 ml-1">
                                {exploreTotal > 0 && `${exploreTotal.toLocaleString('pt-BR')} total`}
                            </span>
                            {exploreFetching && <Loader2 className="w-3 h-3 animate-spin" />}
                        </div>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                        Cancelar
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Chip de projeto ───────────────────────────────────────────────

function ProjectChip({
    label, active, onClick,
}: {
    label: string;
    active: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${
                active
                    ? 'bg-violet-600 text-white border-violet-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-violet-300'
            }`}
        >
            {label}
        </button>
    );
}

// ─── Chip de tipo (filtro card/texto) ──────────────────────────────

function TypeChip({
    label, active, onClick,
}: {
    label: string;
    active: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                active
                    ? 'bg-violet-600 text-white border-violet-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-violet-300'
            }`}
        >
            {label}
        </button>
    );
}

// ─── Sidebar com árvore de pastas (compacta) ───────────────────────

function FoldersSidebar({
    folders, rootTemplateCount, selectedFolderId, onSelectFolder, loading,
}: {
    folders: TemplateFolderNode[];
    rootTemplateCount: number;
    selectedFolderId: number | null;
    onSelectFolder: (id: number | null) => void;
    loading: boolean;
}) {
    return (
        <div className="w-[200px] border-r overflow-y-auto bg-gray-50/50 shrink-0">
            <div className="p-2 space-y-0.5">
                <FolderItem
                    name="Todos (raiz)"
                    icon={<Files className="w-3.5 h-3.5" />}
                    count={rootTemplateCount}
                    depth={0}
                    selected={selectedFolderId === null}
                    onClick={() => onSelectFolder(null)}
                />
                {loading ? (
                    <div className="text-[11px] text-gray-400 px-2 py-2 flex items-center gap-1.5">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        carregando…
                    </div>
                ) : (
                    folders.map((node) => (
                        <FolderBranch
                            key={node.id}
                            node={node}
                            depth={0}
                            selectedFolderId={selectedFolderId}
                            onSelectFolder={onSelectFolder}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

function FolderBranch({
    node, depth, selectedFolderId, onSelectFolder,
}: {
    node: TemplateFolderNode;
    depth: number;
    selectedFolderId: number | null;
    onSelectFolder: (id: number | null) => void;
}) {
    const [open, setOpen] = useState(true);
    const hasChildren = (node.children?.length ?? 0) > 0;

    return (
        <div>
            <div className="flex items-center gap-0.5">
                {hasChildren ? (
                    <button
                        type="button"
                        onClick={() => setOpen(!open)}
                        className="p-0.5 text-gray-400 hover:text-gray-700"
                        title={open ? 'Recolher' : 'Expandir'}
                    >
                        <ChevronDown className={`w-3 h-3 transition-transform ${open ? '' : '-rotate-90'}`} />
                    </button>
                ) : (
                    <span className="w-4" />
                )}
                <FolderItem
                    name={node.name}
                    icon={<Folder className="w-3.5 h-3.5" />}
                    count={node.template_count}
                    depth={depth}
                    selected={selectedFolderId === node.id}
                    onClick={() => onSelectFolder(node.id)}
                />
            </div>
            {hasChildren && open && (
                <div>
                    {node.children!.map((child) => (
                        <FolderBranch
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            selectedFolderId={selectedFolderId}
                            onSelectFolder={onSelectFolder}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function FolderItem({
    name, icon, count, depth, selected, onClick,
}: {
    name: string;
    icon: React.ReactNode;
    count?: number | null;
    depth: number;
    selected: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{ paddingLeft: `${6 + depth * 10}px` }}
            className={`flex-1 flex items-center gap-1.5 px-2 py-1 rounded text-left text-xs transition-colors ${
                selected
                    ? 'bg-violet-100 text-violet-900 font-medium'
                    : 'text-gray-700 hover:bg-white'
            }`}
        >
            <span className={selected ? 'text-violet-600' : 'text-gray-400'}>
                {icon}
            </span>
            <span className="flex-1 truncate">{name}</span>
            {count != null && count > 0 && (
                <span className="text-[10px] text-gray-400">{count}</span>
            )}
        </button>
    );
}

// ─── Row de template do catálogo TI ────────────────────────────────

function TemplateRow({
    template, isFavorite, onClick,
}: {
    template: TemplateLibraryItem;
    isFavorite: boolean;
    onClick: () => void;
}) {
    const isCard = template.template_type === 'generic';
    const hasImage = !!template.image_url;
    const ctr = template.broadcast_ctr;

    const previewBtns = (template.buttons_json ?? []).map((b) => ({
        title: b.title, url: b.url,
    }));

    return (
        <div className="w-full flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-colors group">
            <button
                type="button"
                onClick={onClick}
                className="flex-1 flex items-start gap-3 text-left min-w-0"
            >
                <div className="relative w-16 h-16 rounded-md bg-gradient-to-br from-violet-50 to-sky-50 overflow-hidden flex-shrink-0">
                    {isCard && hasImage ? (
                        <img
                            src={template.image_url!}
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                            {isCard ? (
                                <ImageIcon className="w-5 h-5 text-gray-300" />
                            ) : (
                                <MessageSquare className="w-5 h-5 text-gray-300" />
                            )}
                        </div>
                    )}
                    {isFavorite && (
                        <Star className="absolute top-1 right-1 w-3 h-3 fill-amber-400 text-amber-400 drop-shadow" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 mb-0.5">
                        <h3 className="text-sm font-medium text-gray-900 line-clamp-1 flex-1">
                            {template.title || template.text_content?.slice(0, 60) || `Template #${template.template_id}`}
                        </h3>
                        <Badge variant="outline" className="text-[9px] uppercase shrink-0">
                            {isCard ? 'Card' : 'Texto'}
                        </Badge>
                    </div>
                    {template.subtitle && (
                        <p className="text-xs text-gray-500 line-clamp-1 mb-1">{template.subtitle}</p>
                    )}
                    {!isCard && template.text_content && (
                        <p className="text-xs text-gray-600 line-clamp-2 mb-1 whitespace-pre-line">
                            {template.text_content}
                        </p>
                    )}
                    <div className="flex items-center gap-2 text-[10px] text-gray-400">
                        {template.domain && <span className="font-mono truncate">{template.domain}</span>}
                        {template.language && (
                            <Badge variant="outline" className="text-[9px] uppercase px-1 py-0">
                                {template.language}
                            </Badge>
                        )}
                        {ctr != null && ctr > 0 && (
                            <span className="text-violet-600 font-medium">CTR {ctr.toFixed(1)}%</span>
                        )}
                    </div>
                </div>
            </button>
            <PreviewButton
                title={template.title ?? undefined}
                subtitle={template.subtitle ?? undefined}
                imageUrl={template.image_url ?? undefined}
                text={template.text_content ?? undefined}
                buttons={previewBtns}
            />
        </div>
    );
}

// ─── Row de template próprio (CustomTemplate) ──────────────────────

function CustomTemplateRow({
    template, onClick,
}: {
    template: CustomTemplate;
    onClick: () => void;
}) {
    const isCard = template.template_type === 'generic';
    const hasImage = !!template.image_url;

    const previewBtns = (template.buttons_json ?? []).map((b: any) => ({
        title: b?.title, url: b?.url,
    }));

    return (
        <div className="w-full flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-colors group">
            <button
                type="button"
                onClick={onClick}
                className="flex-1 flex items-start gap-3 text-left min-w-0"
            >
                <div className="relative w-16 h-16 rounded-md bg-gradient-to-br from-violet-50 to-sky-50 overflow-hidden flex-shrink-0">
                    {isCard && hasImage ? (
                        <img
                            src={template.image_url!}
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                            {isCard ? (
                                <ImageIcon className="w-5 h-5 text-violet-300" />
                            ) : (
                                <MessageSquare className="w-5 h-5 text-violet-300" />
                            )}
                        </div>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 mb-0.5">
                        <h3 className="text-sm font-medium text-gray-900 line-clamp-1 flex-1">
                            {template.name || template.title || template.text_content?.slice(0, 60) || `Template #${template.id}`}
                        </h3>
                        <Badge variant="outline" className="text-[9px] uppercase shrink-0">
                            {isCard ? 'Card' : 'Texto'}
                        </Badge>
                    </div>
                    {(template.title || template.subtitle) && (
                        <p className="text-xs text-gray-500 line-clamp-1 mb-1">
                            {[template.title, template.subtitle].filter(Boolean).join(' — ')}
                        </p>
                    )}
                    {!isCard && template.text_content && (
                        <p className="text-xs text-gray-600 line-clamp-2 mb-1 whitespace-pre-line">
                            {template.text_content}
                        </p>
                    )}
                    <div className="flex items-center gap-2 text-[10px] text-gray-400">
                        {template.project_id != null ? (
                            <Badge variant="outline" className="text-[9px] px-1 py-0">
                                Projeto #{template.project_id}
                            </Badge>
                        ) : (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 gap-1">
                                <User className="w-2.5 h-2.5" /> Pessoal
                            </Badge>
                        )}
                        <span className="text-[9px] uppercase tracking-wide">
                            {template.source}
                        </span>
                    </div>
                </div>
            </button>
            <PreviewButton
                title={template.title ?? undefined}
                subtitle={template.subtitle ?? undefined}
                imageUrl={template.image_url ?? undefined}
                text={template.text_content ?? undefined}
                buttons={previewBtns}
            />
        </div>
    );
}

// ─── PreviewButton (ícone Eye + Popover Messenger-like) ────────────

function PreviewButton({
    title, subtitle, imageUrl, text, buttons,
}: {
    title?: string;
    subtitle?: string;
    imageUrl?: string;
    text?: string;
    buttons: Array<{ title?: string; url?: string }>;
}) {
    const [open, setOpen] = useState(false);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={() => setOpen(false)}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
                    className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-violet-700 hover:bg-violet-50 transition-colors"
                    title="Pré-visualizar"
                    aria-label="Pré-visualizar"
                >
                    <Eye className="w-4 h-4" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                side="left"
                align="center"
                sideOffset={8}
                className="p-2 w-auto border-gray-200 shadow-xl"
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <MiniPreviewBubble
                    title={title}
                    subtitle={subtitle}
                    imageUrl={imageUrl}
                    text={text}
                    buttons={buttons}
                />
            </PopoverContent>
        </Popover>
    );
}

function MiniPreviewBubble({
    title, subtitle, imageUrl, text, buttons,
}: {
    title?: string;
    subtitle?: string;
    imageUrl?: string;
    text?: string;
    buttons: Array<{ title?: string; url?: string }>;
}) {
    const hasContent = title || subtitle || text || imageUrl || buttons.length > 0;
    if (!hasContent) {
        return (
            <div className="text-xs text-gray-400 px-3 py-2">
                Sem conteúdo pra pré-visualizar.
            </div>
        );
    }
    return (
        <div className="bg-gray-50 rounded-2xl rounded-tl-sm border border-gray-200 overflow-hidden max-w-[280px]">
            {imageUrl && (
                <div className="aspect-[1.91/1] bg-gray-100 overflow-hidden">
                    <img src={imageUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
            )}
            <div className="p-2.5 space-y-0.5">
                {title && (
                    <p className="text-[13px] font-semibold text-gray-900 line-clamp-2 leading-tight">
                        {title}
                    </p>
                )}
                {subtitle && (
                    <p className="text-[11px] text-gray-600 line-clamp-2 leading-tight">
                        {subtitle}
                    </p>
                )}
                {text && (
                    <p className="text-[12px] text-gray-900 line-clamp-6 leading-snug whitespace-pre-line">
                        {text}
                    </p>
                )}
            </div>
            {buttons.length > 0 && (
                <div className="border-t bg-white">
                    {buttons.slice(0, 3).map((b, i) => (
                        <div
                            key={i}
                            className={`px-3 py-1.5 text-center text-[11px] text-violet-600 font-medium truncate ${
                                i > 0 ? 'border-t border-gray-100' : ''
                            }`}
                        >
                            {b.title || '(sem texto)'}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Skeleton + Empty ──────────────────────────────────────────────

function ListSkeleton() {
    return (
        <div className="space-y-2 p-2">
            {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
            ))}
        </div>
    );
}

function EmptyState({ tab, hasQuery }: { tab: Tab; hasQuery: boolean }) {
    if (tab === 'mine' && !hasQuery) {
        return (
            <div className="text-center py-10 px-6">
                <FolderTreeIcon className="w-8 h-8 mx-auto text-violet-300 mb-2" />
                <p className="text-sm font-medium text-gray-700">Nenhum template aqui</p>
                <p className="text-xs text-gray-500 mt-1">
                    Vá em <strong>Biblioteca de modelos</strong> pra criar templates em pastas.
                </p>
            </div>
        );
    }
    return (
        <div className="text-center py-10 px-6">
            <BookOpen className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            <p className="text-sm font-medium text-gray-700">
                {hasQuery ? 'Nenhum modelo com essa busca' : 'Nenhum modelo disponível'}
            </p>
        </div>
    );
}
